// The /leads Triage tab, as pure functions: which rows go where, in what
// order, and when a lead is late. Rows come from the `get_leads_triage` RPC
// (supabase/migrations/20261030130000_leads_triage_rpc.sql).

export type TriageSection = 'qualified_open' | 'new_form' | 'long_call' | 'cleanup';
export type QualifiedSubgroup = 'death_no_form' | 'form_started' | 'no_form';
export type CleanupSubgroup = 'short_call' | 'never_spoken' | 'duplicate';

export interface TriageRow {
    section: TriageSection;
    subgroup: QualifiedSubgroup | CleanupSubgroup | null;
    lead_id: string;
    client_id: number | null;
    contact_name: string | null;
    contact_phone: string | null;
    owner_id: string | null;
    owner_name: string | null;
    owner_initials: string | null;
    market: string | null;
    language: string | null;
    source_channel: string | null;
    has_gclid: boolean;
    deal_type: 'at_need' | 'pre_need' | null;
    timeline: string | null;
    quoted_price: number | null;
    quote_id: string | null;
    funeral_id: number | null;
    contract_sent_at: string | null;
    opened_at: string | null;
    qualified_at: string | null;
    last_contact_at: string | null;
    call_seconds: number | null;
    detail: string | null;
    /** T-229: the AI's read of the lead's calls. Only on long_call and cleanup rows; absent until classified. */
    ai_verdict?: 'likely_qualified' | 'not_qualified' | 'unclear' | null;
    ai_confidence?: number | null;
    ai_reason?: string | null;
    ai_suggested_action?: string | null;
}

const DAY_MS = 86_400_000;

/**
 * How sure the AI must be before a row gets a label. Calibrated on 200 leads
 * staff had already decided: docs/proofs/T-229-triage-ai-label/CALIBRATION.md.
 * At 0.9 "Likely qualified" was right 87% of the time and "Likely not a lead"
 * cleared none of the qualified leads. Raising a value shows fewer labels and
 * needs no re-run.
 */
export const LIKELY_QUALIFIED_MIN_CONFIDENCE = 0.9;
export const LIKELY_NOT_A_LEAD_MIN_CONFIDENCE = 0.9;

/** Days without contact before a qualified lead turns amber. */
export const CONTACT_LIMIT_DAYS = {
    death_no_form: 1,
    at_need: 3,
    pre_need: 7,
} as const;

/** A form nobody has answered turns amber after this many days. */
export const FORM_LIMIT_DAYS = 1;

export function daysSince(iso: string | null, now: Date): number | null {
    if (!iso) return null;
    const t = Date.parse(iso);
    if (Number.isNaN(t)) return null;
    return Math.max(0, (now.getTime() - t) / DAY_MS);
}

/** "5 h" under a day, "3 d" after. */
export function formatAge(days: number | null): string {
    if (days === null) return '—';
    if (days < 1) return `${Math.max(1, Math.round(days * 24))} h`;
    return `${Math.floor(days)} d`;
}

export function contactLimitDays(row: TriageRow): number {
    if (row.subgroup === 'death_no_form') return CONTACT_LIMIT_DAYS.death_no_form;
    return row.deal_type === 'pre_need' ? CONTACT_LIMIT_DAYS.pre_need : CONTACT_LIMIT_DAYS.at_need;
}

/**
 * Days since we last had contact with a qualified lead. No call/SMS/email at
 * all counts from the day they were qualified.
 */
export function daysSinceContact(row: TriageRow, now: Date): number | null {
    return daysSince(row.last_contact_at ?? row.qualified_at, now);
}

export function isOverdue(row: TriageRow, now: Date): boolean {
    if (row.section === 'qualified_open') {
        const d = daysSinceContact(row, now);
        return d !== null && d > contactLimitDays(row);
    }
    if (row.section === 'new_form') {
        const d = daysSince(row.opened_at, now);
        return d !== null && d > FORM_LIMIT_DAYS;
    }
    return false;
}

/** The one status word a row shows. */
export function statusWord(row: TriageRow): string {
    switch (row.section) {
        case 'qualified_open':
            if (row.subgroup === 'death_no_form') return 'Death occurred';
            if (row.subgroup === 'form_started') return row.contract_sent_at ? 'Contract sent' : 'Form started';
            // Type and timeline show as pills on the row.
            return 'Qualified';
        case 'new_form':
            return 'No reply';
        case 'long_call':
            return 'Spoken';
        case 'cleanup':
            if (row.subgroup === 'duplicate') return 'Duplicate';
            if (row.subgroup === 'never_spoken') return 'Never spoken';
            return 'Short call';
    }
}

/** QC / ON from either a quote region ('quebec') or a lead market ('QC_FR'). */
export function marketCode(market: string | null): 'QC' | 'ON' | null {
    if (!market) return null;
    const m = market.toLowerCase();
    if (m === 'quebec' || m.startsWith('qc')) return 'QC';
    if (m === 'ontario' || m === 'on') return 'ON';
    return null;
}

export type OwnerFilter = 'everyone' | 'mine' | 'none';
export type MarketFilter = 'all' | 'QC' | 'ON';
/**
 * At-need vs pre-need comes from the RPC's `deal_type`: the lead's quote type
 * first, then `leads.intent`. Most call leads have neither, so they only show
 * under All.
 */
export type TypeFilter = 'all' | 'at_need' | 'pre_need';

export interface TriageFilters {
    owner: OwnerFilter;
    market: MarketFilter;
    type: TypeFilter;
}

export function matchesFilters(row: TriageRow, filters: TriageFilters, myId: string | null): boolean {
    const { owner, market, type } = filters;
    if (owner === 'mine' && (!myId || row.owner_id !== myId)) return false;
    if (owner === 'none' && row.owner_id) return false;
    if (market !== 'all' && marketCode(row.market) !== market) return false;
    if (type !== 'all' && row.deal_type !== type) return false;
    return true;
}

export function isLikelyQualified(row: TriageRow): boolean {
    // Already qualified on another lead: the person is being looked after, and
    // qualifying this one too would make a duplicate.
    if (row.subgroup === 'duplicate') return false;
    return row.ai_verdict === 'likely_qualified' && (row.ai_confidence ?? 0) >= LIKELY_QUALIFIED_MIN_CONFIDENCE;
}

export function isLikelyNotALead(row: TriageRow): boolean {
    return row.ai_verdict === 'not_qualified' && (row.ai_confidence ?? 0) >= LIKELY_NOT_A_LEAD_MIN_CONFIDENCE;
}

export interface TriageGroups {
    deathNoForm: TriageRow[];
    formStarted: TriageRow[];
    /** All "no form yet" leads; the tab shows only `noFormDue` until expanded. */
    noForm: TriageRow[];
    noFormDue: TriageRow[];
    newForms: TriageRow[];
    longCalls: TriageRow[];
    cleanup: TriageRow[];
}

/** Most recent contact first; rows with no contact at all go last. */
const byRecentContact = (a: TriageRow, b: TriageRow) => {
    const ta = a.last_contact_at ? Date.parse(a.last_contact_at) : NaN;
    const tb = b.last_contact_at ? Date.parse(b.last_contact_at) : NaN;
    if (Number.isNaN(ta)) return Number.isNaN(tb) ? 0 : 1;
    if (Number.isNaN(tb)) return -1;
    return tb - ta;
};

/** The rows the AI says to look at come first; last contact decides the rest. */
const byLikelyThenRecentContact = (a: TriageRow, b: TriageRow) =>
    Number(isLikelyQualified(b)) - Number(isLikelyQualified(a)) || byRecentContact(a, b);

/**
 * Every section and sub-group is sorted by last contact, most recent first. The
 * two call sections put "Likely qualified" rows ahead of that.
 */
export function groupTriage(rows: TriageRow[], now: Date): TriageGroups {
    const of = (section: TriageSection, subgroup?: string) =>
        rows
            .filter(r => r.section === section && (subgroup === undefined || r.subgroup === subgroup))
            .sort(byRecentContact);

    const noForm = of('qualified_open', 'no_form');

    return {
        deathNoForm: of('qualified_open', 'death_no_form'),
        formStarted: of('qualified_open', 'form_started'),
        noForm,
        noFormDue: noForm.filter(r => isOverdue(r, now)),
        newForms: of('new_form'),
        longCalls: of('long_call').sort(byLikelyThenRecentContact),
        cleanup: of('cleanup').sort(byLikelyThenRecentContact),
    };
}

/** Pill colours, matching the MUI chip colours in the Qualified Follow-ups table. */
export type PillTone = 'red' | 'green' | 'amber' | 'blue' | 'grey' | 'accent';

export interface Pill {
    label: string;
    tone: PillTone;
    /** Full wording, for the hover title. */
    title?: string;
}

/** At-Need / Pre-Need, as in the Qualified Follow-ups table. Unknown → no pill. */
export function typePill(row: TriageRow): Pill | null {
    if (row.deal_type === 'at_need') return { label: 'At-Need', tone: 'red' };
    if (row.deal_type === 'pre_need') return { label: 'Pre-Need', tone: 'green' };
    return null;
}

/**
 * The quote's timeline, coloured as in Qualified Follow-ups. The label drops
 * the "(1-7 days)" range to keep the row on one line; the hover shows it.
 */
export function timelinePill(row: TriageRow): Pill | null {
    const t = row.timeline?.trim();
    if (!t) return null;
    const short = t.replace(/\s*\(.*\)$/, '');
    if (t === 'Passed') return { label: t, tone: 'red', title: t };
    if (t.startsWith('Imminent')) return { label: 'Imminent', tone: 'amber', title: t };
    if (t.startsWith('Near')) return { label: 'Near future', tone: 'blue', title: t };
    if (t.startsWith('Preplanning')) return { label: 'Pre-planning', tone: 'grey', title: t };
    return { label: short || t, tone: 'grey', title: t };
}

/** The AI's label, with its reason on hover. Nothing unless it's confident. */
export function aiPill(row: TriageRow): Pill | null {
    const title = row.ai_reason ?? undefined;
    if (isLikelyQualified(row)) return { label: 'Likely qualified', tone: 'accent', title };
    if (isLikelyNotALead(row)) return { label: 'Likely not a lead', tone: 'grey', title };
    return null;
}

/** One line of context under the name (type and timeline are pills, not text). */
export function contextLine(row: TriageRow): string {
    const parts: string[] = [];
    // A labelled row leads with why, since the column truncates. It replaces the
    // call summary it was read from.
    const reason = aiPill(row) ? row.ai_reason?.replace(/\s+/g, ' ').trim() : null;
    if (reason) parts.push(reason);
    const market = marketCode(row.market);
    if (market) parts.push(row.language ? `${market} ${row.language.toUpperCase()}` : market);
    if (row.quoted_price) parts.push(`quoted $${Math.round(row.quoted_price).toLocaleString('en-CA')}`);
    if (row.call_seconds) parts.push(`${Math.max(1, Math.round(row.call_seconds / 60))}-min call`);
    if (!reason && row.detail) parts.push(row.detail.replace(/\s+/g, ' ').trim());
    return parts.join(' · ');
}
